/**
 * "My approvals" (19 Oct, the approver's view; 22 Oct review slides 8-9).
 *
 * Only what waits for the signed-in approver, each with what they need to
 * decide it: who it is for and what access, the reference user and the roles
 * chosen (by Backoffice for a New hire, by the requester on a Change), why,
 * who asked, how long it has waited and when it is due. Approve is one click;
 * Reject asks for a comment first. The card (`components/approval-card`) is
 * the same one the case page shows (19 Oct, UI review finding 4). The decision goes through the case page's
 * own path (`decideAccessApproval`): the HelpDesk records it and casts the
 * Ivanti vote, so ITSM stays the record. A "Decided" tab keeps the history.
 *
 * The approval email, reminders and Teams cards link here with `?case=<id>`:
 * that case comes first; when nothing there waits for the reader, the link
 * goes on to the case page, which stays reachable from every card.
 */

import { GraduationCap, Icon } from "@norican/icons";
import { Alert, Tabs } from "@norican/react";
import { Link, Navigate, useSearchParams } from "react-router";

import { EmptyStateBlock, ErrorState, LoadingState, PageContainer } from "@/components/common";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";

import { useMyApprovals } from "./api";
import { ApprovalCard } from "./components/approval-card";
import { HOW_APPROVING_WORKS_PATH } from "./how-approving-works-page";
import { focusFirst, landing } from "./lib/my-approvals";

export function MyApprovalsPage() {
  const { t } = useT("tickets");
  const [params] = useSearchParams();
  const focus = params.get("case");
  const query = useMyApprovals();
  useDocumentTitle(t("access.myApprovals.title"));
  const data = query.data;

  if (query.isPending) {
    return (
      <PageContainer title={t("access.myApprovals.title")}>
        <LoadingState />
      </PageContainer>
    );
  }
  if (query.isError || data === undefined) {
    return (
      <PageContainer title={t("access.myApprovals.title")}>
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </PageContainer>
    );
  }
  const waiting = focusFirst(data.waiting ?? [], focus);
  const decided = data.decided ?? [];
  const where = landing(focus, waiting, decided);
  if (where.kind === "case") {
    return <Navigate to={where.path} replace />;
  }
  return (
    <PageContainer
      title={t("access.myApprovals.title")}
      subtitle={t("access.myApprovals.subtitle")}
    >
      <div className="flex flex-col gap-4">
        {/* 29 Oct: the one-minute guide, for approvers new to it. */}
        <Link
          to={HOW_APPROVING_WORKS_PATH}
          className="text-accent-fg-subtle inline-flex items-center gap-1 self-start text-sm underline-offset-2 hover:underline"
        >
          <Icon icon={GraduationCap} size="xs" />
          {t("access.myApprovals.howLink")}
        </Link>
        {data.result?.state === "PARTIAL" ? (
          <Alert tone="neutral">{t("access.myApprovals.partial")}</Alert>
        ) : null}
        <Tabs.Root defaultValue="waiting">
          <Tabs.List aria-label={t("access.myApprovals.title")}>
            <Tabs.Tab value="waiting">
              {t("access.myApprovals.tabWaiting", { n: waiting.length })}
            </Tabs.Tab>
            <Tabs.Tab value="decided">
              {t("access.myApprovals.tabDecided", { n: decided.length })}
            </Tabs.Tab>
            <Tabs.Indicator />
          </Tabs.List>
          <Tabs.Panel value="waiting" className="pt-4">
            {waiting.length === 0 ? (
              <EmptyStateBlock
                title={t("access.myApprovals.emptyWaiting")}
                description={t("access.myApprovals.emptyWaitingHint")}
              />
            ) : (
              <ul className="m-0 flex list-none flex-col gap-4 p-0">
                {waiting.map((card, index) => (
                  <li key={`${card.case_id}-${card.n}-${card.need.need_id}`}>
                    <ApprovalCard
                      card={card}
                      highlighted={card.case_id === focus}
                      // 19 Oct (finding 10): the linked approval is announced and focused.
                      focused={index === 0 && card.case_id === focus}
                      rejectionEmail={data.rejection_email === true}
                    />
                  </li>
                ))}
              </ul>
            )}
          </Tabs.Panel>
          <Tabs.Panel value="decided" className="pt-4">
            {decided.length === 0 ? (
              <EmptyStateBlock title={t("access.myApprovals.emptyDecided")} description={null} />
            ) : (
              <ul className="m-0 flex list-none flex-col gap-4 p-0">
                {decided.map((card) => (
                  <li key={`${card.case_id}-${card.n}-${card.need.need_id}`}>
                    <ApprovalCard card={card} />
                  </li>
                ))}
              </ul>
            )}
          </Tabs.Panel>
        </Tabs.Root>
      </div>
    </PageContainer>
  );
}
